import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, X as XIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import HcahpsStars from "@/components/HcahpsStars";
import api from "@/lib/api";

// Human-friendly one-liners for each HCAHPS measure key. Falls back to the
// measure's own `name` field for anything we don't map.
const MEASURE_HINT = {
  communication_nurses: "How well nurses listen and explain",
  communication_doctors: "How clearly doctors communicate",
  responsiveness: "How quickly staff respond to needs",
  medicines: "How medicines are explained",
  discharge_info: "Quality of discharge instructions",
  care_transition: "Understanding of self-care after discharge",
  cleanliness: "Cleanliness of the hospital environment",
  quietness: "Quietness of the hospital environment",
  overall_rating: "Overall hospital rating (0–10)",
  recommend: "Would recommend to friends and family",
  pain_management: "How well pain was managed",
};

function scoreColor(score) {
  if (score >= 85) return "#16a34a";
  if (score >= 70) return "#eab308";
  return "#dc2626";
}

export default function HcahpsHospitalDialog({ hospital, open, onOpenChange, allHospitals = [] }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [compareId, setCompareId] = useState("");
  const [compareDetail, setCompareDetail] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  // Reset comparison whenever the primary hospital changes.
  useEffect(() => {
    setCompareId("");
    setCompareDetail(null);
  }, [hospital?.id]);

  useEffect(() => {
    if (!open || !hospital?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/hospitals/${hospital.id}/hcahps`);
        if (!cancelled) setDetail(data);
      } catch (err) {
        console.error("Failed to load HCAHPS detail:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, hospital?.id]);

  useEffect(() => {
    if (!compareId) { setCompareDetail(null); return; }
    let cancelled = false;
    (async () => {
      setCompareLoading(true);
      try {
        const { data } = await api.get(`/hospitals/${compareId}/hcahps`);
        if (!cancelled) setCompareDetail(data);
      } catch (err) {
        console.error("Failed to load comparison HCAHPS detail:", err);
      } finally {
        if (!cancelled) setCompareLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [compareId]);

  const measures = useMemo(() => detail?.measures || [], [detail]);
  const compareMeasuresByKey = useMemo(() => {
    const map = new Map();
    (compareDetail?.measures || []).forEach((m) => map.set(m.key, m));
    return map;
  }, [compareDetail]);

  const compareHospital = useMemo(
    () => allHospitals.find((h) => h.id === compareId) || null,
    [allHospitals, compareId]
  );

  const compareOptions = useMemo(
    () => allHospitals
      .filter((h) => h.id !== hospital?.id && (h.hcahps_score ?? null) !== null)
      .sort((a, b) => (b.hcahps_score || 0) - (a.hcahps_score || 0)),
    [allHospitals, hospital?.id]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="hcahps-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle className="font-serif text-2xl text-primary leading-tight">
                {hospital?.name || "Hospital details"}
              </DialogTitle>
              <DialogDescription className="mt-1">
                {hospital?.city}{hospital?.beds ? ` · ${hospital.beds} beds` : ""} · Patient experience across all HCAHPS measures
              </DialogDescription>
            </div>
            {hospital?.id ? (
              <Link
                to={`/hospitals/${hospital.id}`}
                className="shrink-0 inline-flex items-center gap-1 text-xs text-primary underline"
                data-testid="hcahps-dialog-profile-link"
              >
                Full profile <ExternalLink size={11} />
              </Link>
            ) : null}
          </div>
        </DialogHeader>

        {loading && !detail ? (
          <div className="py-14 text-center text-sm text-muted-foreground">Loading measures…</div>
        ) : measures.length === 0 ? (
          <div className="py-14 text-center text-sm text-muted-foreground">
            No HCAHPS measures published for this hospital yet.
          </div>
        ) : (
          <>
            <div className="mt-2 flex items-center gap-6 rounded-md bg-muted/40 px-4 py-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Overall score</div>
                <div className="font-serif text-3xl text-primary">{hospital?.hcahps_score ?? "—"}</div>
              </div>
              <div className="w-px h-10 bg-border" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Stars</div>
                <div className="mt-1"><HcahpsStars stars={hospital?.hcahps_stars || 0} size={16} showValue /></div>
              </div>
              <div className="w-px h-10 bg-border hidden md:block" />
              <div className="hidden md:block">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Best measure</div>
                <div className="mt-1 text-sm text-primary">
                  {[...measures].sort((a, b) => (b.score || 0) - (a.score || 0))[0]?.name}
                </div>
              </div>
              <div className="w-px h-10 bg-border hidden md:block" />
              <div className="hidden md:block">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Weakest measure</div>
                <div className="mt-1 text-sm text-primary">
                  {[...measures].sort((a, b) => (a.score || 0) - (b.score || 0))[0]?.name}
                </div>
              </div>
            </div>

            {/* Compare-with selector */}
            <div className="mt-4 rounded-md border border-border bg-white p-3 flex flex-wrap items-center gap-3" data-testid="hcahps-compare-bar">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground shrink-0">Compare with</div>
              <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                <Select value={compareId} onValueChange={setCompareId}>
                  <SelectTrigger className="h-9 flex-1" data-testid="hcahps-compare-select">
                    <SelectValue placeholder="Pick another hospital…" />
                  </SelectTrigger>
                  <SelectContent>
                    {compareOptions.map((h) => (
                      <SelectItem key={h.id} value={h.id}>
                        {h.name} · {h.city}{h.state && h.state !== "Other" ? `, ${h.state}` : ""} · {h.hcahps_score}
                      </SelectItem>
                    ))}
                    {compareOptions.length === 0 && (
                      <div className="px-3 py-2 text-xs text-muted-foreground">No other hospitals available.</div>
                    )}
                  </SelectContent>
                </Select>
                {compareId ? (
                  <button
                    type="button"
                    onClick={() => setCompareId("")}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs text-muted-foreground hover:text-primary"
                    data-testid="hcahps-compare-clear"
                  >
                    <XIcon size={11} /> Clear
                  </button>
                ) : null}
              </div>
              {compareHospital ? (
                <div className="flex items-center gap-3 text-xs" data-testid="hcahps-compare-summary">
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-primary" /> {hospital?.name} · <b>{hospital?.hcahps_score}</b>
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-[#c45f4a]" /> {compareHospital.name} · <b>{compareHospital.hcahps_score}</b>
                  </span>
                  <span className={"font-medium " + ((hospital?.hcahps_score || 0) >= (compareHospital.hcahps_score || 0) ? "text-emerald-600" : "text-red-600")}>
                    Δ {(hospital?.hcahps_score || 0) - (compareHospital.hcahps_score || 0)}
                  </span>
                </div>
              ) : null}
              {compareLoading && <span className="text-[11px] text-muted-foreground">Loading…</span>}
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {measures.map((m) => {
                const c = compareMeasuresByKey.get(m.key);
                const delta = c ? (m.score || 0) - (c.score || 0) : null;
                return (
                <div
                  key={m.key}
                  className="rounded-md border border-border bg-white p-3"
                  data-testid={`hcahps-measure-${m.key}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-primary leading-snug">{m.name}</div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground leading-snug">
                        {MEASURE_HINT[m.key] || (m.kind === "single" ? "Single-item measure" : "Composite measure")}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-serif text-xl leading-none" style={{ color: scoreColor(m.score) }}>{m.score}</div>
                      <div className="mt-1"><HcahpsStars stars={m.stars} size={11} /></div>
                    </div>
                  </div>

                  {/* Primary hospital bar */}
                  <div className="mt-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-primary shrink-0" title={hospital?.name} />
                    <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${Math.max(0, Math.min(100, m.score))}%`, background: scoreColor(m.score) }}
                      />
                    </div>
                  </div>

                  {/* Comparison hospital bar */}
                  {c ? (
                    <>
                      <div className="mt-1.5 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[#c45f4a] shrink-0" title={compareHospital?.name} />
                        <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${Math.max(0, Math.min(100, c.score))}%`, background: "#c45f4a" }}
                          />
                        </div>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-[11px]">
                        <span className="text-muted-foreground">{compareHospital?.name}</span>
                        <span className="flex items-center gap-2">
                          <span className="text-muted-foreground">{c.score}</span>
                          <span className={"px-1.5 py-0.5 rounded text-[10px] font-medium " + (delta >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700")}>
                            {delta >= 0 ? "+" : ""}{delta}
                          </span>
                        </span>
                      </div>
                    </>
                  ) : null}
                </div>
                );
              })}
            </div>

            <div className="mt-4 text-[11px] text-muted-foreground leading-relaxed">
              HCAHPS Star Ratings follow the CMS April 2022 fact sheet. Composite measures aggregate multiple survey questions;
              single-item measures come from a single question. Higher is better.
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
